import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { WeeklyCountdown } from "../components/Countdown";
import Reveal, { Label } from "../components/Reveal";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";
import { useLang } from "../context/LanguageContext";

export default function Weekly() {
  const [w, setW] = useState(null);
  const [winner, setWinner] = useState(null);
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useLang();

  useEffect(() => {
    api.get("/public/weekly").then((r) => setW(r.data)).catch(() => {});
    api.get("/public/winner").then((r) => setWinner(r.data)).catch(() => {});
  }, []);

  const stats = w ? [
    { v: w.ideas_count, l: t("wk_stat_ideas") },
    { v: w.studied, l: t("wk_stat_studied") },
    // Présélectionnés : pourcentage réglable (admin) des idées proposées affichées.
    { v: Math.round((Number(w.ideas_count) || 0) * (Number(w.presel_percent ?? 7) / 100)), l: t("wk_stat_presel") },
    { v: w.finalists, l: t("wk_stat_finalists") },
  ] : [];

  return (
    <div>
      <Seo title={t("wk_label")} description={Number(w?.winners_count) > 1 ? t("wk_many_desc") : t("wk_one_desc")} path="/challenge" />
      <section className="relative overflow-hidden px-5 pt-36 pb-16 sm:px-8">
        <div className="pointer-events-none absolute -top-20 right-0 h-[500px] w-[500px] rounded-full bg-electric/15 blur-[130px]" />
        <div className="mx-auto max-w-[1400px]">
          <Reveal><Label>{t("wk_label")}</Label></Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-6 max-w-4xl font-display text-4xl font-900 leading-[0.95] tracking-tighter text-white sm:text-6xl">
              {t("wk_h1a")}<br /><span className="text-cyan text-glow">{t("wk_h1b")}</span>
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 pb-16 sm:px-8">
        <div className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((s, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div className="bg-void p-8" data-testid={`weekly-stat-${i}`}>
                <div className="font-display text-5xl font-900 tabular-nums text-white">{s.v}</div>
                <div className="mt-2 text-sm text-ink-secondary">{s.l}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 pb-16 sm:px-8">
        <Reveal>
          <div className="border border-cyan/30 bg-surface p-8 sm:p-12">
            <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr]">
              <div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-ink-tertiary">{t("wk_next")}</div>
                <div className="mt-6"><WeeklyCountdown target={w?.countdown_mode === "fixed" ? w.next_selection : null} /></div>
              </div>
              <div className="flex flex-col justify-center border-white/10 lg:border-l lg:pl-12">
                <div className="font-display text-2xl font-700 text-white">{Number(w?.winners_count) > 1 ? t("wk_many_title").replace("{n}", w.winners_count) : t("wk_one_title")}</div>
                <p className="mt-3 text-sm text-ink-secondary">
                  {Number(w?.winners_count) > 1 ? t("wk_many_desc") : t("wk_one_desc")}
                </p>
                <button
                  onClick={() => nav(user ? "/app/deposer" : "/inscription")}
                  data-testid="weekly-page-cta"
                  className="group mt-6 inline-flex w-fit items-center gap-2 bg-cyan px-6 py-3.5 text-sm font-600 text-void transition-all hover:-translate-y-0.5 hover:glow-cyan"
                >
                  {t("wk_cta")} <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {w?.categories && (
        <section className="mx-auto max-w-[1400px] px-5 pb-24 sm:px-8">
          <Reveal><Label>{t("wk_cat_label")}</Label></Reveal>
          <div className="mt-8 grid gap-5 md:grid-cols-2">
            {w.categories.map((c, i) => (
              <div key={i} data-testid={`weekly-cat-${i}`}>
                <div className="mb-2 flex items-center justify-between text-sm">
                  <span className="text-white">{c.name}</span>
                  <span className="tabular-nums text-cyan">{c.percent}%</span>
                </div>
                <div className="h-1.5 w-full bg-elevated">
                  <motion.div className="h-full bg-cyan" initial={{ width: 0 }} whileInView={{ width: `${c.percent}%` }} viewport={{ once: true }} transition={{ duration: 1, delay: i * 0.06 }} />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {winner?.name && (
        <section className="border-t border-white/10 bg-surface py-24">
          <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
            <Reveal><Label>{t("wk_winner_label")}</Label></Reveal>
            <div className="mt-8 grid gap-10 lg:grid-cols-[1fr_1.4fr]">
              <Reveal>
                <div className="relative aspect-[9/12] overflow-hidden border border-cyan/30">
                  <img src={winner.image} alt={winner.name} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-void/90 to-transparent" />
                  <div className="absolute bottom-5 left-5"><span className="text-4xl">🏆</span></div>
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <h2 className="font-display text-4xl font-900 text-white">{winner.name}</h2>
                <div className="mt-2 text-sm text-ink-secondary">{t("wk_by")} {winner.creator} · {winner.country}</div>
                <p className="mt-6 text-base leading-relaxed text-white">{winner.description}</p>
                <p className="mt-4 text-sm leading-relaxed text-ink-secondary"><span className="text-cyan">{t("wk_problem")}</span>{winner.problem}</p>
                <div className="mt-8 inline-block border border-cyan/40 bg-cyan/5 px-4 py-2 text-xs uppercase tracking-widest text-cyan">
                  {t("wk_dev_free")}
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
